import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  CreateGoalRequest,
  GoalAssigneeType,
  GoalMetric,
  GoalPeriod,
  GoalResponse,
  UpdateGoalRequest,
} from '@troia-v3/types';

import { Drawer, Button, Input, Select, Textarea } from '@/shared/components/ui';
import type { SelectOption } from '@/shared/components/ui';
import { userService } from '@/modules/users/services/userService';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useCreateGoal, useUpdateGoal } from '../hooks/useGoals';

interface GoalFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  goal?: GoalResponse | null;
  /**
   * Meta de origem para DUPLICAR: pré-preenche todos os campos e sugere o
   * PRÓXIMO período (mês/trimestre/ano seguinte). Cria uma nova meta — o usuário
   * pode trocar responsável/empresa e período antes de confirmar. Ignorado
   * quando `goal` (edição) está setado.
   */
  duplicateFrom?: GoalResponse | null;
  initialPeriod?: GoalPeriod;
  initialYear?: number;
  initialMonthOrQuarter?: number;
}

/**
 * Deriva year + monthOrQuarter do periodStart e avança para o PRÓXIMO período.
 * - month: mês+1 (dez → jan do ano seguinte)
 * - quarter: Q+1 (Q4 → Q1 do ano seguinte)
 * - year: ano+1
 */
function nextPeriodFrom(
  period: GoalPeriod,
  periodStart: string,
): { year: number; monthOrQuarter: number } {
  const startStr = String(periodStart).slice(0, 10);
  const startDate = new Date(`${startStr}T00:00:00Z`);
  let year = startDate.getUTCFullYear();
  let monthOrQuarter: number;
  if (period === 'month') {
    monthOrQuarter = startDate.getUTCMonth() + 2; // +1 (0-index→1) +1 (próximo)
    if (monthOrQuarter > 12) { monthOrQuarter = 1; year += 1; }
  } else if (period === 'quarter') {
    monthOrQuarter = Math.floor(startDate.getUTCMonth() / 3) + 2;
    if (monthOrQuarter > 4) { monthOrQuarter = 1; year += 1; }
  } else {
    year += 1;
    monthOrQuarter = 1;
  }
  return { year, monthOrQuarter };
}

const METRIC_OPTIONS: SelectOption[] = [
  { value: 'revenue', label: 'Receita (R$)' },
  { value: 'deals', label: 'Negócios fechados' },
  { value: 'calls', label: 'Ligações realizadas' },
  { value: 'meetings', label: 'Reuniões realizadas' },
  { value: 'qualifiedLeads', label: 'Leads qualificados' },
  { value: 'proposalsSent', label: 'Propostas enviadas' },
];

const ASSIGNEE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'user', label: 'Vendedor' },
  { value: 'team', label: 'Equipe' },
  { value: 'company', label: 'Empresa' },
];

const PERIOD_OPTIONS: SelectOption[] = [
  { value: 'month', label: 'Mensal' },
  { value: 'quarter', label: 'Trimestral' },
  { value: 'year', label: 'Anual' },
];

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/**
 * Compute periodStart/periodEnd from period type + year + month/quarter index.
 *
 * - month: full month (day 1 to last day)
 * - quarter (1-4): months 1/4/7/10 → 3/6/9/12
 * - year: full year
 */
function computePeriodBounds(
  period: GoalPeriod,
  year: number,
  monthOrQuarter: number,
): { start: string; end: string } {
  if (period === 'year') {
    return { start: `${year}-01-01`, end: `${year}-12-31` };
  }
  if (period === 'quarter') {
    const startMonth = (monthOrQuarter - 1) * 3;
    const endMonth = startMonth + 2;
    const lastDay = new Date(Date.UTC(year, endMonth + 1, 0)).getUTCDate();
    return {
      start: `${year}-${String(startMonth + 1).padStart(2, '0')}-01`,
      end: `${year}-${String(endMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    };
  }
  // month
  const lastDay = new Date(Date.UTC(year, monthOrQuarter, 0)).getUTCDate();
  return {
    start: `${year}-${String(monthOrQuarter).padStart(2, '0')}-01`,
    end: `${year}-${String(monthOrQuarter).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  };
}

/**
 * Drawer de criação/edição de Goals.
 *
 * Hooks de fetch (users/teams) são CONDICIONAIS ao `isOpen` AND ao `assigneeType`
 * correspondente, conforme CLAUDE.md NUNCA #14.
 */
export const GoalFormDrawer: React.FC<GoalFormDrawerProps> = ({
  isOpen,
  onClose,
  goal,
  duplicateFrom,
  initialPeriod,
  initialYear,
  initialMonthOrQuarter,
}) => {
  const isEdit = !!goal;
  const isDuplicate = !isEdit && !!duplicateFrom;
  const createMut = useCreateGoal();
  const updateMut = useUpdateGoal();

  const [assigneeType, setAssigneeType] = useState<GoalAssigneeType>('user');
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [metric, setMetric] = useState<GoalMetric>('revenue');
  const [period, setPeriod] = useState<GoalPeriod>('month');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [monthOrQuarter, setMonthOrQuarter] = useState<number>(new Date().getMonth() + 1);
  const [targetValue, setTargetValue] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Conditional dropdowns (NUNCA #14: condicionar fetch ao isOpen)
  const usersQuery = useQuery({
    queryKey: ['users', 'dropdown', { status: 'active' }],
    queryFn: () => userService.getUsersForDropdown({ status: 'active' }),
    enabled: isOpen && assigneeType === 'user',
    staleTime: 10 * 60 * 1000,
  });

  const teamsQuery = useTeamsDropdown({ enabled: isOpen && assigneeType === 'team' });

  useEffect(() => {
    if (!isOpen) return;
    if (goal) {
      setAssigneeType(goal.assigneeType);
      setAssigneeId(goal.assigneeId ?? '');
      setMetric(goal.metric);
      setPeriod(goal.period);
      const startStr = String(goal.periodStart).slice(0, 10);
      const startDate = new Date(`${startStr}T00:00:00Z`);
      setYear(startDate.getUTCFullYear());
      if (goal.period === 'month') {
        setMonthOrQuarter(startDate.getUTCMonth() + 1);
      } else if (goal.period === 'quarter') {
        setMonthOrQuarter(Math.floor(startDate.getUTCMonth() / 3) + 1);
      } else {
        setMonthOrQuarter(1);
      }
      setTargetValue(String(goal.targetValue));
      setNotes(goal.notes ?? '');
    } else if (duplicateFrom) {
      // Duplicar: copia tudo da origem e sugere o próximo período.
      setAssigneeType(duplicateFrom.assigneeType);
      setAssigneeId(duplicateFrom.assigneeId ?? '');
      setMetric(duplicateFrom.metric);
      setPeriod(duplicateFrom.period);
      const { year: ny, monthOrQuarter: nmq } = nextPeriodFrom(duplicateFrom.period, String(duplicateFrom.periodStart));
      setYear(ny);
      setMonthOrQuarter(nmq);
      setTargetValue(String(duplicateFrom.targetValue));
      setNotes(duplicateFrom.notes ?? '');
    } else {
      setAssigneeType('user');
      setAssigneeId('');
      setMetric('revenue');
      setPeriod(initialPeriod ?? 'month');
      setYear(initialYear ?? new Date().getFullYear());
      setMonthOrQuarter(initialMonthOrQuarter ?? new Date().getMonth() + 1);
      setTargetValue('');
      setNotes('');
    }
  }, [isOpen, goal, duplicateFrom, initialPeriod, initialYear, initialMonthOrQuarter]);

  const periodSubOptions = useMemo<SelectOption[]>(() => {
    if (period === 'year') return [];
    if (period === 'quarter') return [1, 2, 3, 4].map((q) => ({ value: String(q), label: `Q${q}` }));
    return MONTHS.map((m, i) => ({ value: String(i + 1), label: m }));
  }, [period]);

  const assigneeOptions = useMemo<SelectOption[]>(() => {
    if (assigneeType === 'user') {
      return (usersQuery.data ?? []).map((u) => ({ value: u.id, label: u.name }));
    }
    if (assigneeType === 'team') {
      return (teamsQuery.data ?? []).map((t) => ({ value: t.id, label: t.name }));
    }
    return [];
  }, [assigneeType, usersQuery.data, teamsQuery.data]);

  const isSubmitting = createMut.isPending || updateMut.isPending;

  const handleSubmit = async (): Promise<void> => {
    const target = Number(targetValue);
    if (!Number.isFinite(target) || target <= 0) return;
    if (assigneeType !== 'company' && !assigneeId) return;

    const { start, end } = computePeriodBounds(period, year, monthOrQuarter);

    const resolvedAssigneeId: string | null = assigneeType === 'company' ? null : (assigneeId || null);
    const trimmedNotes = notes.trim();

    if (isEdit && goal) {
      const payload: UpdateGoalRequest = {
        assigneeType,
        assigneeId: resolvedAssigneeId,
        metric,
        period,
        periodStart: start,
        periodEnd: end,
        targetValue: target,
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      };
      await updateMut.mutateAsync({ id: goal.id, data: payload });
    } else {
      const payload: CreateGoalRequest = {
        assigneeType,
        assigneeId: resolvedAssigneeId,
        metric,
        period,
        periodStart: start,
        periodEnd: end,
        targetValue: target,
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      };
      await createMut.mutateAsync(payload);
    }
    onClose();
  };

  const footer = (
    <>
      <Button
        variant="secondary"
        onClick={onClose}
        disabled={isSubmitting}
        data-testid="goal-form-cancel"
      >
        Cancelar
      </Button>
      <Button
        onClick={handleSubmit}
        loading={isSubmitting}
        disabled={isSubmitting}
        data-testid="goal-form-submit"
      >
        {isEdit ? 'Salvar' : isDuplicate ? 'Duplicar' : 'Criar'}
      </Button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={isEdit ? 'Editar meta' : isDuplicate ? 'Duplicar meta' : 'Nova meta'}
      footer={footer}
    >
      <div className="flex flex-col gap-4 px-6 py-5" data-testid="goal-form-drawer">
        <Select
          label="Atribuir a"
          value={assigneeType}
          onChange={(value) => {
            setAssigneeType(value as GoalAssigneeType);
            setAssigneeId('');
          }}
          options={ASSIGNEE_TYPE_OPTIONS}
          data-testid="goal-form-assignee-type"
        />

        {assigneeType !== 'company' && (
          <Select
            label={assigneeType === 'user' ? 'Vendedor' : 'Equipe'}
            value={assigneeId}
            onChange={setAssigneeId}
            options={assigneeOptions}
            placeholder={assigneeType === 'user' ? 'Selecione um vendedor' : 'Selecione uma equipe'}
            data-testid="goal-form-assignee-id"
          />
        )}

        <Select
          label="Métrica"
          value={metric}
          onChange={(value) => setMetric(value as GoalMetric)}
          options={METRIC_OPTIONS}
          data-testid="goal-form-metric"
        />

        <Select
          label="Período"
          value={period}
          onChange={(value) => {
            setPeriod(value as GoalPeriod);
            setMonthOrQuarter(1);
          }}
          options={PERIOD_OPTIONS}
          data-testid="goal-form-period"
        />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--theme-text)' }}
            >
              Ano
            </label>
            <Input
              type="number"
              min={2000}
              max={2100}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              data-testid="goal-form-year"
            />
          </div>
          {period !== 'year' && (
            <Select
              label={period === 'month' ? 'Mês' : 'Trimestre'}
              value={String(monthOrQuarter)}
              onChange={(value) => setMonthOrQuarter(Number(value))}
              options={periodSubOptions}
              data-testid="goal-form-month-quarter"
            />
          )}
        </div>

        <div>
          <label
            className="block text-sm font-medium mb-1.5"
            style={{ color: 'var(--theme-text)' }}
          >
            Valor da meta
          </label>
          <Input
            type="number"
            min={0}
            step="0.01"
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            placeholder={metric === 'revenue' ? 'Ex: 100000' : 'Ex: 50'}
            data-testid="goal-form-target-value"
          />
        </div>

        <Textarea
          label="Observações (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Notas internas sobre a meta"
          data-testid="goal-form-notes"
        />
      </div>
    </Drawer>
  );
};

GoalFormDrawer.displayName = 'GoalFormDrawer';
